import React, { useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { prop } from 'ramda';

import Overlay from '@core/js/components/common/Overlay';

import { SvgIcon } from '@interlayer/js/app/components/common/SvgIcon';
import { MenuItem } from '@interlayer/js/app/components/main/DefaultMenu/components/MenuItem/MenuItem';
import { Skeleton } from '@interlayer/js/app/components/main/Skeleton';

import { IMenuItemConfig } from '@core/js/components/system/App/store/types';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';

import { useSwitchMobileMenu } from '@interlayer/js/app/components/main/DefaultHeader/components/HeaderMobile/hooks';
import { TYPES_BLOCK } from '@interlayer/js/app/components/main/DefaultMenu/components/MenuItem/constants';
import { MENU_CLASSES } from '@interlayer/js/app/components/main/DefaultMenu/constants';
import { SKELETON_TYPES } from '@interlayer/js/app/components/main/Skeleton/constants';
import { useInDesktop } from '@interlayer/js/app/hooks/device/hooks/useMediaQuery';
import { useMenu } from '@interlayer/js/app/hooks/menu/useMenu';
import { clsx } from '@interlayer/js/app/utils/clsx.util';
import { useClickOutSide } from '@interlayer/js/app/utils/common.util';
import { useUpdateEffect } from '@interlayer/js/hooks/useUpdateEffect';

import { TSideMenuLeftProps } from './types';

import style from './SideMenuLeft.scss';

export const SideMenuLeft: React.FC<TSideMenuLeftProps> = ({ isInDom, isVisible }) => {
    const switchMobileMenu = useSwitchMobileMenu();
    const isLoadingMenuItems = useAppSelector(prop('isLoadingMenuItems'));
    const { pathname } = useLocation();
    const mobileMenuRef = useRef(null);
    const isDesktop = useInDesktop();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));

    const checkItemMenu = useCallback((item: IMenuItemConfig): boolean => {
        if (!isLoggedIn && item.cssClasses.includes(MENU_CLASSES.IS_LOGOUT)) {
            return false;
        }

        return [MENU_CLASSES.ONLY_FOR_RIGHT, MENU_CLASSES.ONLY_FOR_TOP, MENU_CLASSES.NOT_FOR_LEFT]
            .every(child => !item.cssClasses.includes(child));
    }, [isLoggedIn]);

    const filteredMenu = useMenu(checkItemMenu);

    useClickOutSide(mobileMenuRef, () => switchMobileMenu(false));

    useUpdateEffect(() => {
        switchMobileMenu(false);
    }, [pathname, switchMobileMenu]);

    if (!isInDom) {
        return (
            <div className={style.menuWrap} ref={mobileMenuRef} />
        );
    }

    return (
        <>
            <div className={clsx(style.menuWrap, { [style.isOpened]: isVisible })} ref={mobileMenuRef}>
                {!isDesktop && (
                    <SvgIcon
                        onClick={() => switchMobileMenu(false)}
                        className={style.close}
                        type="close-popup"
                    />
                )}

                <div className={style.menu}>
                    {isLoadingMenuItems
                        ? (
                            <Skeleton configuration={[{ type: SKELETON_TYPES.menuItem, count: 8 }]} />
                        )
                        : filteredMenu.map((item) => (
                            <MenuItem
                                data={item}
                                key={item.url + item.friendlyURL}
                                typeBlock={TYPES_BLOCK.left}
                            />
                        ))
                    }
                </div>
            </div>

            {isVisible && (
                <Overlay
                    className={clsx(style.overlay, { [style.overlayDesktop]: isDesktop })}
                    onClick={() => switchMobileMenu(false)}
                />
            )}
        </>
    );
};
